import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mapPayerJCodesToOptions } from "../utils/narAgentGridJCodeOptions";
import useListJCodesByPayer from "./useListJCodesByPayer";
import useNarAgentGridAuthGridSearch from "./useNarAgentGridAuthGridSearch";
import useNarAgentGridInsuranceOptions from "./useNarAgentGridInsuranceOptions";
import useNarAgentGridOrgTabs from "./useNarAgentGridOrgTabs";

/**
 * Composes NAR Agent Grid page state: org tabs, filters, J-code options, and search.
 */
export const useNarAgentGridLayout = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const { tabs, loading: isLoadingOrgTabs } = useNarAgentGridOrgTabs();
  const { insuranceOptions } = useNarAgentGridInsuranceOptions();
  const { apiData, isLoadingAuthGrid, fetchAuthGrid, clearResults } =
    useNarAgentGridAuthGridSearch();

  const [activeTab, setActiveTab] = useState("");
  const [selectedInsurance, setSelectedInsurance] = useState("");
  const [selectedJCodes, setSelectedJCodes] = useState<string[]>([]);

  const {
    data: payerJCodes,
    loading: isLoadingJCodes,
    error: jCodesError,
  } = useListJCodesByPayer({ payerName: selectedInsurance });

  const jCodeOptions = useMemo(
    () => mapPayerJCodesToOptions(payerJCodes),
    [payerJCodes],
  );

  useEffect(() => {
    if (!activeTab && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  useEffect(() => {
    if (activeTab) {
      clearResults();
      setSelectedInsurance("");
      setSelectedJCodes([]);
    }
  }, [activeTab, clearResults]);

  useEffect(() => {
    setSelectedJCodes([]);
  }, [selectedInsurance]);

  useEffect(() => {
    if (jCodesError) {
      console.error("Error fetching payer J-codes:", jCodesError);
    }
  }, [jCodesError]);

  const runSearch = useCallback(
    (options?: { silent?: boolean }) =>
      fetchAuthGrid(
        {
          payerName: selectedInsurance,
          jCodes: selectedJCodes,
          clientId: activeTab,
        },
        options,
      ),
    [activeTab, fetchAuthGrid, selectedInsurance, selectedJCodes],
  );

  const fetchAuthGridSilent = useCallback(
    (): Promise<void> => runSearch({ silent: true }),
    [runSearch],
  );

  return {
    tableRef,
    orgTabs: {
      tabs,
      loading: isLoadingOrgTabs,
      activeTab,
      setActiveTab,
    },
    filters: {
      activeTab,
      insuranceOptions,
      selectedInsurance,
      setSelectedInsurance,
      selectedJCodes,
      setSelectedJCodes,
      jCodeOptions,
      isLoadingJCodes,
    },
    search: {
      isLoadingAuthGrid,
      isSearchDisabled: isLoadingAuthGrid || isLoadingJCodes || !activeTab,
      fetchAuthGrid: runSearch,
      fetchAuthGridSilent,
    },
    results: {
      apiData,
      clientId: activeTab,
    },
  };
};

export default useNarAgentGridLayout;
